import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { ENQUIRY_RANGES, FLEET_TIERS } from "@/data/products";
import { INDUSTRIES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", phone: "", assets: "", industry: "", country: "", message: "" };
const inputCls = "h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-sm text-tracksy-ink placeholder:text-tracksy-muted focus:border-tracksy-orange focus:outline-none focus:ring-2 focus:ring-tracksy-orange/20";

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-tracksy-sub">{label}</span>
    {children}
  </label>
);

const FleetEnquiry = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "company", "email", "assets", "industry", "country"];
    const missing = required.filter((k) => !form[k].trim());
    if (missing.length) {
      toast.error("Please complete the required fields", { description: missing.join(", ") });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/fleet-enquiry`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Request failed");
      }
      setDone(true);
      toast.success("Enquiry received", { description: "Tracksy Fleet will be in touch with pricing." });
    } catch (err) {
      toast.error("Couldn't send your enquiry", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="fleet-enquiry-section" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow="Request fleet pricing" title="Tell us about your fleet." desc="For orders of 100+ devices: tailored pricing, a purchase-order process and a dedicated contact. Under 100? Simply order online in any quantity." />
          {!compact && (
            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-3">
                {FLEET_TIERS.map((t) => (
                  <li key={t.id} className="flex items-center justify-between rounded-2xl border border-black/5 bg-white px-5 py-3.5 shadow-subtle">
                    <span className="font-heading text-sm font-bold text-tracksy-ink">{t.name} <span className="font-body text-xs font-semibold text-tracksy-sub">· {t.range}</span></span>
                    <span className={`text-xs font-bold ${t.selfServe ? "text-tracksy-green" : "text-tracksy-orange"}`}>{t.selfServe ? "Buy online" : "Request pricing"}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div data-testid="fleet-enquiry-success" className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-[2rem] border border-black/5 bg-white p-10 text-center shadow-subtle">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-tracksy-orange/10"><Send className="h-6 w-6 text-tracksy-orange" /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-tracksy-ink">Thanks, {form.name.split(" ")[0]}.</h3>
              <p className="mt-2 max-w-sm text-sm text-tracksy-sub">We've received your enquiry for {form.company}. Tracksy Fleet will reply to {form.email} with volume pricing.</p>
            </div>
          ) : (
            <form data-testid="fleet-enquiry-form" onSubmit={submit} className="grid grid-cols-1 gap-5 rounded-[2rem] border border-black/5 bg-white p-6 shadow-subtle sm:grid-cols-2 sm:p-8">
              <Field label="Name *" id="fe-name"><input id="fe-name" data-testid="fleet-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" /></Field>
              <Field label="Company *" id="fe-company"><input id="fe-company" data-testid="fleet-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Business name" /></Field>
              <Field label="Email *" id="fe-email"><input id="fe-email" type="email" data-testid="fleet-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" /></Field>
              <Field label="Phone" id="fe-phone"><input id="fe-phone" data-testid="fleet-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+61 ..." /></Field>
              <Field label="Number of vehicles / assets *" id="fe-assets">
                <select id="fe-assets" data-testid="fleet-assets" className={inputCls} value={form.assets} onChange={set("assets")}>
                  <option value="">Select a range</option>
                  {ENQUIRY_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Industry *" id="fe-industry">
                <select id="fe-industry" data-testid="fleet-industry" className={inputCls} value={form.industry} onChange={set("industry")}>
                  <option value="">Select an industry</option>
                  {INDUSTRIES.map((i) => <option key={i.id} value={i.name}>{i.name}</option>)}
                  <option value="Other">Other</option>
                </select>
              </Field>
              <Field label="Country *" id="fe-country"><input id="fe-country" data-testid="fleet-country" className={inputCls} value={form.country} onChange={set("country")} placeholder="Australia" /></Field>
              <div className="sm:col-span-2">
                <Field label="Message" id="fe-message">
                  <textarea id="fe-message" data-testid="fleet-message" rows={4} className={`${inputCls} h-auto py-3`} value={form.message} onChange={set("message")} placeholder="What are you looking to track?" />
                </Field>
              </div>
              <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-tracksy-muted">Tailored pricing for orders of 100+ devices.</p>
                <motion.button type="submit" data-testid="fleet-submit" disabled={loading} whileTap={{ scale: 0.97 }} className="inline-flex items-center justify-center gap-2 rounded-full bg-tracksy-orange px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-glow transition-colors hover:bg-tracksy-orangeHover disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Request fleet pricing
                </motion.button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default FleetEnquiry;
